/**
 * ExpauseSheet Types
 *
 * Type definitions for the ExpauseSheet component system.
 * Supports bottom sheets (mobile/portrait), menus, and dialogs (desktop/landscape).
 */

import type { ReactNode } from 'react';

/**
 * Response types matching Flutter's BottomSheetResultType
 */
export enum ExpauseSheetResponseType {
  mention = 'mention',
  publishDraft = 'publishDraft',
  share = 'share',
  confirm = 'confirm',
  editUser = 'editUser',
  reportContent = 'reportContent',
  reportUser = 'reportUser',
  blockConversation = 'blockConversation',
  unBlockConversation = 'unBlockConversation',
  muteConversation = 'muteConversation',
  unMuteConversation = 'unMuteConversation',
  chatGift = 'chatGift',
  subscribeUser = 'subscribeUser',
  rechargeWallet = 'rechargeWallet',
  accountInfo = 'accountInfo',
  unlockContent = 'unlockContent',
}

/**
 * Generic data container for sheet response
 */
export type ExpauseSheetResponseData = Record<string, unknown>;

/**
 * Sheet response returned when a sheet closes with an action
 */
export interface ExpauseSheetResponse {
  type: ExpauseSheetResponseType;
  data?: ExpauseSheetResponseData;
}

/**
 * Display mode for the sheet
 */
export type SheetDisplayMode = 'auto' | 'bottomSheet' | 'dialog';

/**
 * Button orientation for CTA buttons
 */
export type ButtonOrientation = 'row' | 'column';

/**
 * Individual button configuration
 */
export interface SheetButton {
  /** Button label text */
  text: string;

  /** Button click handler */
  onClick: () => void | Promise<void>;

  /** Optional custom text color (defaults to theme) */
  textColor?: string;

  /** Optional button style variant (defaults to 'primary' in landscape, 'label' in portrait) */
  variant?: 'label' | 'primary' | 'danger';

  /** Whether button is disabled */
  disabled?: boolean;

  /** Whether clicking this button should auto-close the sheet (default: true) */
  closeOnClick?: boolean;

  /** Whether to show loading indicator on this button */
  loading?: boolean;

  /**
   * Optional `data-qa-id` override for this button. When omitted, CTAButtons
   * falls back to positional ids (`sheet-cta-primary` for the first button,
   * `sheet-cta-secondary` for the rest) so the qa-tester can always locate the
   * sheet's primary/secondary actions. Test-only; never affects rendering.
   */
  qaId?: string;
}

/**
 * Call-to-action buttons configuration
 */
export interface SheetCTA {
  /** Array of buttons to display */
  buttons: SheetButton[];

  /** Button display orientation */
  orientation?: ButtonOrientation;
}

/**
 * Configuration for showing a sheet
 */
export interface ExpauseSheetConfig {
  /** Content to display in the sheet */
  content: ReactNode;

  /** Optional title for the sheet (can be a string or React element) */
  title?: ReactNode;

  /** Optional top bar title (centered, smaller text) */
  topBarTitle?: string;

  /** Whether to show drag indicator (default: true for bottom sheet) */
  dragIndicator?: boolean;

  /** Optional CTA buttons configuration */
  cta?: SheetCTA;

  /** Force display as dialog (overrides auto detection) */
  forceDialog?: boolean;

  /** Force display as bottom sheet (overrides auto detection) */
  forceBottomSheet?: boolean;

  /** Optional callback when sheet is closed */
  onClose?: () => void;

  /** Whether clicking outside closes the sheet (default: true) */
  closeOnClickOutside?: boolean;

  /** Optional custom max height for bottom sheet (e.g., '75%', '400px'). Default: '65vh' */
  maxHeight?: string;

  /** Optional fixed height for sheet (e.g., '500px', '80vh'). Overrides maxHeight and sets both height and maxHeight to the same value. */
  fixedHeight?: string;

  /** Optional custom width for menu/dialog (e.g., '400px', '90vw') */
  width?: string;

  /** Optional custom max width for dialog only (e.g., '500px', '80vw') */
  maxWidth?: string;

  /** Whether content is scrollable (default: true) */
  scrollable?: boolean;

  /**
   * Whether the sheet can be dismissed via swipe-to-dismiss gesture (default: true).
   * When false, disables swipe-to-dismiss functionality on mobile but keeps overlay tap-to-close.
   * Useful for sheets containing components with their own scroll management (e.g., steppers).
   */
  dismissible?: boolean;

  /**
   * Optional container element for the sheet portal.
   * When provided, the sheet will render into this container instead of document.body.
   * Useful for page-scoped sheets that should hide/show with the page.
   */
  container?: HTMLElement | null;

  /**
   * Optional screen key identifying which route opened this sheet.
   * Used to determine if back button should close the sheet or navigate away.
   */
  screenKey?: string;

  /**
   * Optional stable identifier for this sheet, used to build the rendered
   * container's `data-qa-id` (`sheet-<qaId>`) so the qa-tester can `wait_for`
   * a specific sheet to open. When omitted the surface falls back to a
   * generic `sheet-bottom` / `sheet-dialog` id. Test-only; never affects
   * rendering.
   */
  qaId?: string;
}

/**
 * Internal sheet state managed by the provider
 */
export interface SheetState extends ExpauseSheetConfig {
  isOpen: boolean;
  id: string;
}

/**
 * Context value for ExpauseSheet
 */
export interface ExpauseSheetContextValue {
  /** Stack of sheet states (newest on top) */
  sheets: SheetState[];

  /** Show a new sheet (adds to stack) */
  showSheet: (config: ExpauseSheetConfig) => void;

  /** Hide the topmost sheet */
  hideSheet: () => void;

  /** Hide all sheets */
  hideAllSheets: () => void;
}
